<template>
  <div>
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <b-modal
      :id="'event-date-modal-'+ type"
      title="Select Event Date"
      ok-title="Send"
      @ok="handleNoaSubmit"
    >
      <b-form-group
        label="Event Date"
        label-for="event-date"
      >
        <b-input-group>
          <flat-pickr
            id="event-date"
            ref="datePicker"
            v-model="eventDate"
            :config="dateConfig"
            class="form-control"
            placeholder="Enter date and time YYYY-MM-DD HH:mm"
          />
          <b-input-group-append>
            <b-button
              variant="outline-secondary"
              @click="$refs.datePicker.fp.toggle()"
            >
              <feather-icon icon="CalendarIcon" />
            </b-button>
            <b-button
              variant="outline-secondary"
              @click="eventDate = null"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
    </b-modal>
    <b-row v-if="canEdit">
      <b-col class="mt-2">
        <b-card-text>
          Selected items for status messages.
        </b-card-text>
      </b-col>
      <b-col class="text-right">
        <b-button
          v-if="type === 'arrived_to_facility'"
          variant="outline-success"
          class="m-1"
          :disabled="!done || excelLoading"
          @click="handleExcelDownload"
        >
          Download arrived as Excel
        </b-button>
        <b-button
          variant="outline-success"
          class="m-1"
          :disabled="disabledBtn"
          @click="sendMessages"
        >
          Send status messages
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <parcels-tree
          :containers="containers"
          :shipment="shipment"
          :data-loading="loading"
          :data-loading-message="loadingMessage"
          :filters="filters"
          :filter="type"
          :not-allowed-filters="['arrived']"
          @selection="selection"
          @updateFilters="handleTreeFilters"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BButton,
  BCol,
  BRow,
  BCardText,
  BFormGroup,
  BModal,
  BInputGroup,
  BInputGroupAppend,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import ParcelsTree from '@/views/shipments/declarations/ParcelsTree.vue'
import shipmentModel from '@/views/shipments/shipmentModel'
import handleError from '@/views/components/errorHandler'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { EventBus } from '@core/utils/utils'
import { searchQuery } from '@core/utils/filter'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'

export default {
  components: {
    ConfirmModal,
    BButton,
    BCol,
    BRow,
    BCardText,
    ParcelsTree,
    flatPickr,
    BFormGroup,
    BModal,
    BInputGroup,
    BInputGroupAppend,
  },
  mixins: [excelDownloadMixin],
  props: ['shipmentId', 'type', 'done', 'eventTypes'],
  data() {
    return {
      loading: false,
      loadingMessage: 'Loading',
      shipment: {},
      containers: [],
      selectedAll: false,
      selectedContainers: [],
      selectedParcels: [],
      filters: {},
      eventDate: null,
      dateConfig: {
        enableTime: true,
        time_24hr: true,
        altInput: true,
        altFormat: 'Y-m-d H:i',
        dateFormat: 'Y-m-d H:i',
        maxDate: new Date(),
        allowInput: true,
        clickOpens: false,
      },
    }
  },
  computed: {
    excelDownloadUrl() {
      return `${process.env.VUE_APP_API_URL}/v1/shipments/${this.shipmentId}/parcels/arrivedInFacility/excel`
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    disabledBtn() {
      return (
        this.selectedAll === false
        && this.selectedContainers.length === 0
        && this.selectedParcels.length === 0
      )
        || this.containers.length === 0
        || this.loadingMessage !== 'Loading'
    },
    currentEventType() {
      return (this.eventTypes || []).find(et => et.slug === this.type) || {}
    },
  },
  async created() {
    await this.getShipment()
    if (this.loadingMessage === 'Loading') {
      this.fetchData()
    }
    EventBus.$on('general-shipment-update', this.fetchData)
    EventBus.$on('shipment-event-processing', this.eventsProcessing)
    EventBus.$on('shipment-event-finished', this.processingFinished)
  },
  beforeDestroy() {
    EventBus.$off('general-shipment-update', this.fetchData)
    EventBus.$off('shipment-event-processing', this.eventsProcessing)
    EventBus.$off('shipment-event-finished', this.processingFinished)
  },
  methods: {
    handleTreeFilters(filters) {
      this.filters = filters
      this.fetchData()
    },
    eventsProcessing() {
      this.loading = true
      this.loadingMessage = 'Events dispatching'
    },
    processingFinished() {
      this.loading = false
      this.loadingMessage = 'Loading'
      this.fetchData()
    },
    getShipment() {
      return new Promise(resolve => {
        const cachedData = shipmentModel().getCachedShipment(this.shipmentId)
        if (cachedData === null) {
          shipmentModel()
            .getShipment(this.shipmentId, this)
            .then(response => {
              shipmentModel().cacheShipment(response.data)
              this.$set(this, 'shipment', response.data)
              resolve() // Resolve after shipment is fetched
            })
        } else {
          this.$set(this, 'shipment', cachedData)
          resolve() // Resolve immediately if data is cached
        }
      })
    },
    fetchData(activeTab) {
      if (!activeTab || activeTab === this.type) {
        this.loading = true
        this.$http.get(searchQuery(`/v1/api/companies/${this.shipment.company_id}/shipments/${this.shipmentId}/events/${this.type}`, this.filters))
          .then(response => {
            this.containers = response.data.data
            this.loading = false
          })
          .catch(error => {
            handleError(error, this.$toast)
          })
      }
    },
    selection(data) {
      this.selectedAll = data.selectedAll
      this.selectedContainers = data.selectedContainers
      this.selectedParcels = data.selectedParcels
    },
    sendMessages() {
      if (this.currentEventType.allow_timestamp_change) {
        this.eventDate = null
        this.$bvModal.show(`event-date-modal-${this.type}`)
      } else {
        this.submitEvents()
      }
    },
    handleNoaSubmit() {
      this.submitEvents()
    },
    submitEvents() {
      this.$http.post(`/v1/api/companies/${this.shipment.company_id}/shipments/${this.shipmentId}/events`, {
        all: this.selectedAll,
        containers: this.selectedContainers,
        parcels: this.selectedParcels,
        events: [this.type],
        occurred_at: (this.currentEventType.allow_timestamp_change && this.eventDate) ? this.eventDate : null,
      })
        .then(response => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: response.data.message,
              icon: 'CheckmarkIcon',
              variant: 'success',
            },
          })
          this.fetchData()
          EventBus.$emit('refresh-shipment-statistics')
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
  },
}
</script>

<style lang="scss">

</style>
